const API_URL = process.env.REACT_APP_API_URL || '/api';

async function request(endpoint, options = {}) {
  const response = await fetch(`${API_URL}${endpoint}`, {
    cache: 'no-store',
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    }
  });

  const contentType = response.headers.get('content-type') || '';

  if (!response.ok) {
    const error = contentType.includes('application/json')
      ? await response.json().catch(() => ({ error: 'Request failed' }))
      : { error: `Request failed with status ${response.status}` };
    throw new Error(error.error || 'Request failed');
  }

  if (!contentType.includes('application/json')) {
    throw new Error('API returned HTML instead of JSON. Check that /api is routed to the backend.');
  }

  return response.json();
}

async function downloadRequest(endpoint, filename) {
  const response = await fetch(`${API_URL}${endpoint}`, {
    credentials: 'include',
    cache: 'no-store'
  });

  if (!response.ok) {
    const contentType = response.headers.get('content-type') || '';
    const error = contentType.includes('application/json')
      ? await response.json().catch(() => ({ error: 'Download failed' }))
      : { error: `Download failed with status ${response.status}` };
    throw new Error(error.error || 'Download failed');
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Upload with two-phase progress:
 * Phase 1 (0–80%): browser → backend, tracked via XHR onprogress
 * Phase 2 (80–100%): backend → Azure, smoothly animated
 */
function uploadRequest(endpoint, formData, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}${endpoint}`);
    xhr.withCredentials = true;

    let phase2Interval = null;
    let phase2Progress = 80;

    if (onProgress && xhr.upload) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          // Phase 1: scale 0–100% of upload to 0–80% of total
          const phase1 = Math.round((event.loaded / event.total) * 80);
          onProgress(phase1);

          // When browser→server transfer is done, start phase 2 animation
          if (event.loaded === event.total && !phase2Interval) {
            phase2Interval = setInterval(() => {
              phase2Progress = Math.min(phase2Progress + 1, 98);
              onProgress(phase2Progress);
              if (phase2Progress >= 98) {
                clearInterval(phase2Interval);
                phase2Interval = null;
              }
            }, 150);
          }
        }
      };
    }

    xhr.onload = () => {
      if (phase2Interval) { clearInterval(phase2Interval); phase2Interval = null; }
      if (onProgress) onProgress(100);

      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)); }
        catch (e) { reject(new Error('Upload API returned an invalid response')); }
      } else {
        try {
          const error = JSON.parse(xhr.responseText);
          reject(new Error(error.error || `Upload failed with status ${xhr.status}`));
        } catch (e) {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      }
    };

    xhr.onerror = () => {
      if (phase2Interval) { clearInterval(phase2Interval); phase2Interval = null; }
      reject(new Error('Network error during upload'));
    };
    xhr.onabort = () => {
      if (phase2Interval) { clearInterval(phase2Interval); phase2Interval = null; }
      reject(new Error('Upload cancelled'));
    };

    xhr.send(formData);
  });
}

// Auth
export const authApi = {
  login: (azureToken) => request('/auth/login', {
    method: 'POST',
    headers: { Authorization: `Bearer ${azureToken}` }
  }),
  getMe: () => request('/auth/me'),
  logout: () => request('/auth/logout', { method: 'POST' })
};

// Users (admin)
export const usersApi = {
  getAll: () => request('/users'),
  updateRole: (id, role) => request(`/users/${id}/role`, {
    method: 'PATCH',
    body: JSON.stringify({ role })
  })
};

// Policies
export const policiesApi = {
  getPublished: () => request('/policies/published'),
  getAll: () => request('/policies'),
  // Authenticated proxy URL — always fresh, never expiring in the browser
  viewUrl: (id) => `${API_URL}/policies/${id}/view`,
  downloadUrl: (id) => `${API_URL}/policies/${id}/view?download=1`,
  create: (data) => request('/policies', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  update: (id, data) => request(`/policies/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  delete: (id) => request(`/policies/${id}`, { method: 'DELETE' }),
  togglePublish: (id) => request(`/policies/${id}/toggle-publish`, { method: 'PATCH' }),
  publish: (id) => request(`/policies/${id}/publish`, { method: 'PATCH' }),
  unpublish: (id) => request(`/policies/${id}/unpublish`, { method: 'PATCH' }),
  uploadFile: (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return uploadRequest('/policies/upload', formData, onProgress);
  }
};

// Policy Tags
export const tagsApi = {
  getAll: () => request('/policies/tags'),
  create: (name) => request('/policies/tags', { method: 'POST', body: JSON.stringify({ name }) }),
  delete: (id) => request(`/policies/tags/${id}`, { method: 'DELETE' })
};

// Videos
export const videosApi = {
  getPublished: () => request('/videos/published'),
  getAll: () => request('/videos'),
  // Authenticated proxy URL — always fresh
  viewUrl: (id) => `${API_URL}/videos/${id}/view`,
  downloadUrl: (id) => `${API_URL}/videos/${id}/view?download=1`,
  create: (data) => request('/videos', {
    method: 'POST', 
    body: JSON.stringify(data)
  }),
  update: (id, data) => request(`/videos/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  delete: (id) => request(`/videos/${id}`, { method: 'DELETE' }),
  togglePublish: (id) => request(`/videos/${id}/toggle-publish`, { method: 'PATCH' }),
  publish: (id) => request(`/videos/${id}/publish`, { method: 'PATCH' }),
  unpublish: (id) => request(`/videos/${id}/unpublish`, { method: 'PATCH' }),
  uploadFile: (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return uploadRequest('/videos/upload', formData, onProgress);
  }
};

// Acknowledgements
export const acknowledgementsApi = {
  getMine: () => request('/acknowledgements/me'),
  acknowledge: () => request('/acknowledgements', { method: 'POST' }),
  getReport: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/acknowledgements/report${q ? '?' + q : ''}`);
  },
  exportReport: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    const stamp = new Date().toISOString().slice(0, 10);
    return downloadRequest(`/acknowledgements/export${q ? '?' + q : ''}`, `acknowledgement-report-${stamp}.csv`);
  }
};

// Activity Logs
export const logsApi = {
  getAll: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/logs${q ? '?' + q : ''}`);
  }
};

// API Tokens for external policy access
export const apiTokensApi = {
  getAll: () => request('/api-tokens'),
  create: (name) => request('/api-tokens', {
    method: 'POST',
    body: JSON.stringify({ name })
  }),
  revoke: (id) => request(`/api-tokens/${id}/revoke`, { method: 'PATCH' }),
  delete: (id) => request(`/api-tokens/${id}`, { method: 'DELETE' }),
  externalPoliciesUrl: () => `${API_URL}/external/policies`
};
